/**
 * API Client with automatic token refresh
 */

import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

// Ensure HTTPS in production
let API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
if (typeof window !== 'undefined' && window.location.protocol === 'https:' && API_URL.startsWith('http://')) {
  API_URL = API_URL.replace('http://', 'https://');
  console.warn('API URL upgraded to HTTPS for security');
}

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management - use functions to always get fresh values from localStorage
const getAccessToken = () => localStorage.getItem('access_token');
const getRefreshToken = () => localStorage.getItem('refresh_token');
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    // Check if token expires in the next 30 seconds
    return decoded.exp < (currentTime + 30);
  } catch (error) {
    return true;
  }
};

const refreshAccessToken = async () => {
  try {
    const response = await axios.post(`${API_URL}/auth/refresh`, {
      refresh_token: getRefreshToken(),
    });

    const { access_token, refresh_token: new_refresh_token } = response.data;

    // Save to localStorage (getters will pick these up automatically)
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', new_refresh_token);

    return access_token;
  } catch (error) {
    // Refresh failed, clear tokens and redirect to login
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    window.location.href = '/login';
    throw error;
  }
};

// Request interceptor - add auth token to requests
api.interceptors.request.use(
  async (config) => {
    const currentToken = getAccessToken();
    const currentRefreshToken = getRefreshToken();
    
    // Check if token is expired or about to expire
    if (currentToken && isTokenExpired(currentToken) && currentRefreshToken) {
      if (!isRefreshing) {
        isRefreshing = true;

        try {
          const newToken = await refreshAccessToken();
          isRefreshing = false;
          processQueue(null, newToken);
        } catch (error) {
          isRefreshing = false;
          processQueue(error, null);
          throw error;
        }
      }

      // Wait for token to be refreshed
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        config.headers.Authorization = `Bearer ${token}`;
        return config;
      }).catch((error) => {
        return Promise.reject(error);
      });
    }

    // Add current token to request
    if (currentToken) {
      config.headers.Authorization = `Bearer ${currentToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle 401 errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // If 401 and not already retried
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Wait for the refresh to complete
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch((err) => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();
        isRefreshing = false;
        processQueue(null, newToken);

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        processQueue(refreshError, null);
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API methods
export const authAPI = {
  register: async (email, password, name, inviteCode = null) => {
    const response = await axios.post(`${API_URL}/auth/register`, {
      email,
      password,
      name,
      invite_code: inviteCode,
    });

    const { access_token, refresh_token, user } = response.data;

    // Save tokens to localStorage (getters will pick these up)
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('user', JSON.stringify(user));

    return response.data;
  },

  login: async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, {
      email,
      password,
    });

    const { access_token, refresh_token, user } = response.data;

    // Save tokens to localStorage (getters will pick these up)
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('user', JSON.stringify(user));

    return response.data;
  },

  logout: async () => {
    try {
      const currentRefreshToken = getRefreshToken();
      if (currentRefreshToken) {
        await api.post('/auth/logout', {
          refresh_token: currentRefreshToken,
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear tokens regardless of API call success
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    }
  },

  verifyEmail: async (token) => {
    const response = await axios.post(`${API_URL}/auth/verify-email`, { token });
    return response.data;
  },

  resendVerification: async (email) => {
    const response = await axios.post(`${API_URL}/auth/resend-verification`, { email });
    return response.data;
  },

  forgotPassword: async (email) => {
    const response = await axios.post(`${API_URL}/auth/forgot-password`, { email });
    return response.data;
  },

  resetPassword: async (token, newPassword) => {
    const response = await axios.post(`${API_URL}/auth/reset-password`, {
      token,
      new_password: newPassword,
    });
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  googleLogin: async (credential) => {
    const response = await axios.post(`${API_URL}/auth/google`, {
      credential,
    });

    const { access_token, refresh_token, user } = response.data;

    // Save tokens to localStorage (getters will pick these up)
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('user', JSON.stringify(user));

    return response.data;
  },
};

// Teams API methods
export const teamsAPI = {
  // Get all my teams
  getMyTeams: async () => {
    const response = await api.get('/teams');
    return response.data;
  },

  // Get team details
  getTeam: async (teamId) => {
    const response = await api.get(`/teams/${teamId}`);
    return response.data;
  },

  // Create new team
  createTeam: async (name, sport = 'volleyball', logoUrl = null) => {
    const response = await api.post('/teams', {
      name,
      sport,
      logo_url: logoUrl,
    });
    return response.data;
  },

  // Update team
  updateTeam: async (teamId, data) => {
    const response = await api.put(`/teams/${teamId}`, data);
    return response.data;
  },

  // Delete team
  deleteTeam: async (teamId) => {
    const response = await api.delete(`/teams/${teamId}`);
    return response.data;
  },

  // Regenerate invite codes
  regenerateInviteCodes: async (teamId) => {
    const response = await api.post(`/teams/${teamId}/regenerate-codes`);
    return response.data;
  },

  // Get team roster
  getRoster: async (teamId) => {
    const response = await api.get(`/teams/${teamId}/roster`);
    return response.data;
  },

  // Add player to roster
  addPlayer: async (teamId, playerData) => {
    const response = await api.post(`/teams/${teamId}/roster`, playerData);
    return response.data;
  },

  // Update player
  updatePlayer: async (teamId, playerId, playerData) => {
    const response = await api.put(`/teams/${teamId}/roster/${playerId}`, playerData);
    return response.data;
  },

  // Remove player from roster
  removePlayer: async (teamId, playerId) => {
    const response = await api.delete(`/teams/${teamId}/roster/${playerId}`);
    return response.data;
  },

  // Get team staff
  getStaff: async (teamId) => {
    const response = await api.get(`/teams/${teamId}/staff`);
    return response.data;
  },

  // Add staff member
  addStaff: async (teamId, email, role) => {
    const response = await api.post(`/teams/${teamId}/staff`, { email, role });
    return response.data;
  },

  // Update staff member role
  updateStaff: async (teamId, memberId, role) => {
    const response = await api.put(`/teams/${teamId}/staff/${memberId}`, { role });
    return response.data;
  },

  // Remove staff member
  removeStaff: async (teamId, memberId) => {
    const response = await api.delete(`/teams/${teamId}/staff/${memberId}`);
    return response.data;
  },
};

// Matches API methods
export const matchesAPI = {
  // Get all matches (optionally filter by team)
  getMatches: async (teamId = null) => {
    const params = teamId ? `?team_id=${teamId}` : '';
    const response = await api.get(`/matches${params}`);
    return response.data;
  },

  // Get match details
  getMatch: async (matchId) => {
    const response = await api.get(`/matches/${matchId}`);
    return response.data;
  },

  // Upload video for a match
  uploadVideo: async (teamId, title, videoFile, homeTeam = null, awayTeam = null, onProgress = null) => {
    console.log('[API] Starting video upload...', {
      teamId,
      title,
      fileName: videoFile?.name,
      fileSize: videoFile?.size ? `${(videoFile.size / (1024 * 1024)).toFixed(2)} MB` : 'unknown'
    });

    // Validate file size (warn if > 500MB, but allow up to 2GB)
    const maxSizeMB = 2048; // 2GB
    const fileSizeMB = videoFile?.size / (1024 * 1024);
    if (fileSizeMB > maxSizeMB) {
      throw new Error(`File too large: ${fileSizeMB.toFixed(2)} MB. Maximum size is ${maxSizeMB} MB.`);
    }
    if (fileSizeMB > 500) {
      console.warn(`[API] Large file detected: ${fileSizeMB.toFixed(2)} MB. Upload may take several minutes.`);
    }

    try {
      const formData = new FormData();
      formData.append('team_id', teamId);
      formData.append('title', title);
      formData.append('video', videoFile);
      formData.append('use_modal', 'true'); // Explicitly enable Modal processing
      if (homeTeam) formData.append('home_team', homeTeam);
      if (awayTeam) formData.append('away_team', awayTeam);

      console.log('[API] Sending POST request to /matches/upload...');
      const startTime = Date.now();

      const response = await api.post('/matches/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 1800000, // 30 minutes for large uploads
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percent);
            if (percent % 10 === 0) { // Log every 10%
              console.log(`[API] Upload progress: ${percent}%`);
            }
            if (percent === 100) {
              console.log('[API] File upload complete, waiting for server response...');
            }
          }
        },
      });

      const duration = ((Date.now() - startTime) / 1000).toFixed(1);
      console.log(`[API] Upload completed in ${duration}s`, response.data);
      return response.data;
    } catch (error) {
      console.error('[API] Upload error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        code: error.code
      });

      // Provide more helpful error messages
      if (error.code === 'ECONNABORTED') {
        throw new Error('Upload timed out. The file may be too large. Try a smaller video or check your internet connection.');
      } else if (error.response?.status === 408) {
        throw new Error('Upload timed out on server. Try uploading a smaller file.');
      } else if (error.response?.status === 413) {
        throw new Error('File too large. Maximum upload size exceeded.');
      } else if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      } else if (error.message) {
        throw error;
      } else {
        throw new Error('Upload failed. Please try again.');
      }
    }
  },

  // Start/restart video processing
  processMatch: async (matchId) => {
    const response = await api.post(`/matches/${matchId}/process`);
    return response.data;
  },

  // Get match events
  getEvents: async (matchId, verifiedOnly = false, eventType = null) => {
    let params = [];
    if (verifiedOnly) params.push('verified_only=true');
    if (eventType) params.push(`event_type=${eventType}`);
    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    const response = await api.get(`/matches/${matchId}/events${queryString}`);
    return response.data;
  },

  // Get match statistics
  getStats: async (matchId) => {
    const response = await api.get(`/matches/${matchId}/stats`);
    return response.data;
  },

  // Get video URL
  getVideoUrl: (matchId) => {
    const token = getAccessToken();
    return `${api.defaults.baseURL}/matches/${matchId}/video?token=${token}`;
  },

  // Delete match
  deleteMatch: async (matchId) => {
    const response = await api.delete(`/matches/${matchId}`);
    return response.data;
  },

  // Get match processing progress
  getMatchProgress: async (matchId) => {
    const response = await api.get(`/matches/${matchId}`);
    return response.data;
  },

  // Correct event type (coach-only)
  correctEventType: async (matchId, eventId, correctedEventType, reason = null) => {
    const response = await api.post(`/matches/${matchId}/events/${eventId}/correct`, {
      corrected_event_type: correctedEventType,
      reason: reason,
    });
    return response.data;
  },

  // Link event to player (coach-only)
  linkEventToPlayer: async (matchId, eventId, playerProfileId) => {
    const response = await api.post(`/matches/${matchId}/events/${eventId}/link-player`, {
      player_profile_id: playerProfileId,
    });
    return response.data;
  },

  // Get event corrections (coach-only)
  getEventCorrections: async (matchId) => {
    const response = await api.get(`/matches/${matchId}/corrections`);
    return response.data;
  },

  // Get ball tracks
  getBallTracks: async (matchId) => {
    const response = await api.get(`/matches/${matchId}/ball-tracks`);
    return response.data;
  },

  // Get court detections
  getCourtDetections: async (matchId) => {
    const response = await api.get(`/matches/${matchId}/court-detections`);
    return response.data;
  },

  // Get player links
  getPlayerLinks: async (matchId) => {
    const response = await api.get(`/matches/${matchId}/player-links`);
    return response.data;
  },

  // Get detected errors (coach-only)
  getErrors: async (matchId, verified = null, errorType = null) => {
    let params = [];
    if (verified !== null) params.push(`verified=${verified}`);
    if (errorType) params.push(`error_type=${errorType}`);
    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    const response = await api.get(`/matches/${matchId}/errors${queryString}`);
    return response.data;
  },

  // Get error statistics (coach-only)
  getErrorStats: async (matchId) => {
    const response = await api.get(`/matches/${matchId}/error-stats`);
    return response.data;
  },

  // Correct/verify an error (coach-only)
  correctError: async (matchId, errorId, correctedErrorType, verified = true, reason = null) => {
    const response = await api.post(`/matches/${matchId}/errors/${errorId}/correct`, {
      corrected_error_type: correctedErrorType,
      verified: verified,
      reason: reason,
    });
    return response.data;
  },
};

// Insights API methods
export const insightsAPI = {
  // Get player statistics
  getPlayerStats: async (playerId) => {
    const response = await api.get(`/insights/player/${playerId}/stats`);
    return response.data;
  },

  // Get player recommendations
  getPlayerRecommendations: async (playerId) => {
    const response = await api.get(`/insights/player/${playerId}/recommendations`);
    return response.data;
  },

  // Get team insights
  getTeamInsights: async (teamId) => {
    const response = await api.get(`/insights/team/${teamId}/overview`);
    return response.data;
  },
};

// Helper to set tokens (for use after OAuth)
export const setTokens = (access_token, refresh_token, user) => {
  localStorage.setItem('access_token', access_token);
  localStorage.setItem('refresh_token', refresh_token);
  localStorage.setItem('user', JSON.stringify(user));
};

// Re-export getAccessToken for external use
export { getAccessToken };

export default api;
